"use client";

import { useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { formatPrice } from "@/lib/format-price";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type PriceContent = {
  name?: string;
  description?: string;
  price_ie?: string;
  price_uk?: string;
  monthly_price_ie?: string;
  monthly_price_uk?: string;
  features?: string;
};

type PriceStory = {
  content?: PriceContent;
};

type TreatmentTab = SbBlokData & {
  tab_label?: string;
  price_ref?: string | PriceStory;
  button_label?: string;
  button_link?: StoryblokLink;
};

type TreatmentsSectionBlok = SbBlokData & {
  title?: string;
  intro?: string;
  tabs?: TreatmentTab[];
  bg_color?: string;
  cta_label?: string;
  cta_link?: StoryblokLink;
};

function resolvePrice(ref: TreatmentTab["price_ref"]): PriceContent | undefined {
  if (!ref || typeof ref === "string") return undefined;
  return ref.content;
}

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function TreatmentsSection({
  blok,
}: {
  blok: TreatmentsSectionBlok;
}) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const tabs = useMemo(() => blok.tabs || [], [blok.tabs]);
  const { bg, onDark } = resolveBg(blok.bg_color, "forest");
  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabStripRef = useRef<HTMLDivElement>(null);
  const cta = resolveLink(blok.cta_link, region);
  const showCta = Boolean(blok.cta_label && cta.href !== "#");

  const selectTab = (i: number) => {
    setActiveIndex(i);
    // On the second-to-last tab, scroll the last one into view as well so it's
    // clear there's a further option.
    const target = i === tabs.length - 2 ? i + 1 : i;
    const strip = tabStripRef.current;
    const pill = tabRefs.current[target];
    if (!strip || !pill) return;
    // Centre the pill within the strip by hand — scrollIntoView would also let
    // iPad Safari scroll the page/section sideways, which drifts each click.
    const pillRect = pill.getBoundingClientRect();
    const stripRect = strip.getBoundingClientRect();
    const delta =
      pillRect.left + pillRect.width / 2 - (stripRect.left + stripRect.width / 2);
    strip.scrollTo({ left: strip.scrollLeft + delta, behavior: "smooth" });
  };

  if (tabs.length === 0) return null;

  return (
    <section
      {...storyblokEditable(blok)}
      className={`${bg} overflow-hidden ${onDark ? "text-white" : "text-black"}`}
    >
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(2.75rem,6vw,4.5rem)] pb-[clamp(3rem,5vw,3.75rem)] md:flex md:items-start md:justify-between md:gap-12 lg:gap-20 xl:gap-[120px]">
        <div className="md:flex-1 md:max-w-[467px] md:pt-[65px]">
          {blok.title && (
            <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08]">
              {blok.title}
            </h2>
          )}
          {blok.intro && (
            <p className="text-body mt-4 leading-normal">{blok.intro}</p>
          )}
          {showCta && (
            <a
              href={cta.href}
              {...(cta.target ? { target: cta.target } : {})}
              className="mt-6 hidden h-14 items-center justify-center rounded-lg bg-purple-03 px-7 text-body font-bold text-white transition-colors hover:bg-purple-04 md:inline-flex"
            >
              {blok.cta_label}
            </a>
          )}
        </div>

        <div className="md:min-w-0 md:flex-1">
          <div
            ref={tabStripRef}
            className="mt-7 -mx-[clamp(1.25rem,4vw,2.5rem)] overflow-x-auto px-[clamp(1.25rem,4vw,2.5rem)] [-ms-overflow-style:none] scrollbar-none [&::-webkit-scrollbar]:hidden md:mx-0 md:mt-0 md:px-0 lg:overflow-visible"
          >
            <div className="flex w-max gap-2 lg:w-auto lg:flex-wrap">
              {tabs.map((tab, i) => {
                const isActive = i === activeIndex;
                return (
                  <button
                    key={tab._uid as string}
                    ref={(el) => {
                      tabRefs.current[i] = el;
                    }}
                    onClick={() => selectTab(i)}
                    className={`flex h-11 shrink-0 items-center rounded-full px-6 text-button font-semibold leading-[1.054] ${
                      isActive
                        ? onDark
                          ? "bg-white text-black"
                          : "bg-black text-white"
                        : onDark
                          ? "border border-white/60 bg-transparent text-white hover:bg-white/15"
                          : "bg-black/10 text-black hover:bg-black/20"
                    }`}
                  >
                    {tab.tab_label}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            // --card-w is computed off the container so every card scales with
            // available space; --card-step is how much each rear card peeks out.
            className="mt-6 grid w-full [--card-step:8px] [--card-w:calc(100%-(var(--card-count)-1)*var(--card-step))] md:mt-[30px] md:[--card-step:30px]"
            style={{ "--card-count": tabs.length } as React.CSSProperties}
          >
            {tabs.map((tab, i) => {
              const rank = (i - activeIndex + tabs.length) % tabs.length;
              const isActive = rank === 0;
              const price = resolvePrice(tab.price_ref);
              const cardPrice = formatPrice(
                region === "uk" ? price?.price_uk : price?.price_ie,
                region,
              );
              const cardMonthly = formatPrice(
                region === "uk"
                  ? price?.monthly_price_uk
                  : price?.monthly_price_ie,
                region,
              );
              const link = resolveLink(tab.button_link, region);
              return (
                <div
                  key={tab._uid as string}
                  {...storyblokEditable(tab)}
                  aria-hidden={!isActive}
                  style={{
                    gridArea: "1 / 1",
                    zIndex: tabs.length - rank,
                    transform: `translateX(calc(${rank} * var(--card-step))) translateY(calc(${rank} * 6px)) scale(${1 - rank * 0.025})`,
                    transformOrigin: "top right",
                  }}
                  className={`flex min-h-[273px] w-(--card-w) flex-col justify-between gap-[44px] rounded-[12px] px-[21px] pt-[25px] pb-7 text-black shadow-[0_6px_4px_rgba(0,0,0,0.45)] transition-[transform,background-color] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] md:pr-[27px] ${
                    isActive ? "bg-white" : "bg-dusk-01"
                  }`}
                >
                  <div className="flex flex-col gap-[18px] md:max-w-[400px]">
                    {price?.name && (
                      <div className="text-h6 font-bold leading-[1.054] tracking-[0.02em]">
                        {price.name}
                      </div>
                    )}
                    {(cardPrice || cardMonthly) && (
                      <div className="flex flex-col">
                        {cardPrice && (
                          <div className="text-h4 font-extrabold leading-[1.054] tracking-[0.02em] text-purple-03">
                            {cardPrice}
                          </div>
                        )}
                        {cardMonthly && (
                          <div className="text-button leading-[1.4] text-[#6b7280]">
                            or from {cardMonthly} per month
                          </div>
                        )}
                      </div>
                    )}
                    {price?.description && (
                      <p className="text-body leading-[1.4]">
                        {price.description}
                      </p>
                    )}
                  </div>
                  {tab.button_label && (
                    <a
                      href={link.href}
                      {...(link.target ? { target: link.target } : {})}
                      tabIndex={isActive ? 0 : -1}
                      className="inline-flex w-fit items-center gap-[7px] rounded-md text-body font-extrabold leading-[1.054] text-purple-04 hover:underline focus-visible:ring-2 focus-visible:ring-purple-03 focus-visible:ring-offset-2 focus-visible:outline-none"
                    >
                      {tab.button_label}
                      <ArrowIcon />
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {showCta && (
            <a
              href={cta.href}
              {...(cta.target ? { target: cta.target } : {})}
              className="mt-7 flex h-12 items-center justify-center rounded-lg bg-purple-03 px-5 text-body font-bold text-white transition-colors hover:bg-purple-04 md:hidden"
            >
              {blok.cta_label}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
